/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { EuiButton, EuiButtonEmpty, EuiTourStep } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import { billingBudgetsAndNotificationsUrl } from '@/apps/userconsole/urls'

import useBudgetTour from './useBudgetTour'

const BudgetTourFirstStep: React.FunctionComponent<{
  children: JSX.Element
  isUserMenuOpen: boolean
  loggedIn: boolean
}> = ({ children, loggedIn, isUserMenuOpen }) => {
  const { showTour, closeTour, totalSteps } = useBudgetTour()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isStepOpen = showTour && loggedIn && !isUserMenuOpen && !pathname.includes('/billing')

  return (
    <EuiTourStep
      anchorPosition='downCenter'
      repositionOnScroll={true}
      content={
        <FormattedMessage
          id='budget-tour.first-step.content'
          defaultMessage='You can now set budget thresholds for your organization. Receive alerts when your usage approaches a specific limit to avoid surprises on your bill.'
        />
      }
      maxWidth={350}
      isStepOpen={isStepOpen}
      step={1}
      stepsTotal={totalSteps}
      title={
        <FormattedMessage
          id='budget-tour.first-step.title'
          defaultMessage='Track your cloud spend'
        />
      }
      subtitle={
        <FormattedMessage
          id='budget-tour.subtitle'
          defaultMessage='NEW - Budgets and notifications'
        />
      }
      onFinish={closeTour}
      footerAction={[
        <EuiButtonEmpty size='s' color='text' onClick={closeTour}>
          <FormattedMessage id='budget-tour.skip-tour' defaultMessage='Skip tour' />
        </EuiButtonEmpty>,
        <EuiButton
          size='s'
          color='success'
          onClick={() => {
            navigate(billingBudgetsAndNotificationsUrl())
          }}
        >
          <FormattedMessage id='budget-tour.set-budget' defaultMessage='Set budget' />
        </EuiButton>,
      ]}
    >
      {children}
    </EuiTourStep>
  )
}

export default BudgetTourFirstStep
